<script setup lang="ts">
import {
  CheckCircleOutlined,
  CloudUploadOutlined,
  ExclamationCircleOutlined,
  RollbackOutlined,
  SaveOutlined,
} from '@ant-design/icons-vue';
import { notification } from 'ant-design-vue';
import { computed, onUnmounted, reactive, ref } from 'vue';
import { devolverCompromissoHandle } from '~/api/generated/clients/DevolverCompromisso/devolverCompromissoHandle';
import { useConcluirFluxoCompromissoAvulso } from '~/composables/useConcluirFluxoCompromissoAvulso';
import { useAuthenticationStore } from '~/stores/authenticationStore';

interface Props {
  compromissoCodigos?: string[];
  compromisso?: any;
}

const props = defineProps<Props>();
const emit = defineEmits(['close']);

const formRef = ref<any>();
const uploaderRef = ref<any>();

const model = reactive({
  observacao: '',
  arquivos: [] as any[],
});

const { status, result, errorMessage, submit, stopPolling, setLoading } =
  useConcluirFluxoCompromissoAvulso();

// Devolver state
const devolverModalVisible = ref(false);
const devolverStatus = ref<'idle' | 'loading' | 'success' | 'error'>('idle');
const justificativaDevolucao = ref('');

const authenticationStore = useAuthenticationStore();
const buttonsDisabled = computed(() => {
  if (!props.compromisso) {
    return false;
  }
  return authenticationStore.user!.id !== props.compromisso.responsavelCodigo;
});

const isLoading = computed(() => status.value === 'loading');
const isSuccess = computed(() => status.value === 'success');
const isPartial = computed(() => status.value === 'partial');
const isDevolverSuccess = computed(() => devolverStatus.value === 'success');
const showForm = computed(
  () => !isSuccess.value && !isPartial.value && !isDevolverSuccess.value,
);

const isLote = computed(() => {
  const totalCompromissos =
    props.compromissoCodigos?.length ?? (props.compromisso ? 1 : 0);
  return totalCompromissos > 1;
});

const compromissoCodigosComputed = computed(() => {
  if (props.compromissoCodigos?.length) {
    return props.compromissoCodigos;
  }
  return props.compromisso ? [props.compromisso.codigo] : [];
});

async function handleSubmit() {
  try {
    await formRef.value.validate();
  } catch {
    return;
  }

  // Iniciar loading antes do upload
  setLoading();

  // Upload arquivos primeiro
  if (uploaderRef.value) {
    await uploaderRef.value.upload();
  }

  // Mapear arquivos para o formato esperado pelo backend
  const arquivos = model.arquivos.map((arquivo: any) => ({
    uid: arquivo.uid,
    name: arquivo.name,
    size: arquivo.size,
    type: arquivo.type,
  }));

  await submit({
    compromissoCodigos: compromissoCodigosComputed.value,
    observacao: model.observacao,
    arquivos,
  });

  // Mostrar notificacao de erro se falhou
  if (status.value === 'error') {
    notification.error({
      message: errorMessage.value || 'Tivemos um problema inesperado',
      duration: 10,
    });
  }
}

function openDevolverModal() {
  justificativaDevolucao.value = '';
  devolverModalVisible.value = true;
}

async function handleDevolver() {
  if (!justificativaDevolucao.value.trim()) {
    notification.warning({
      message: 'Justificativa é obrigatória para devolver o compromisso',
      duration: 5,
    });
    return;
  }

  devolverStatus.value = 'loading';

  try {
    await devolverCompromissoHandle({
      codigoCompromisso: compromissoCodigosComputed.value[0],
      justificativaDevolucao: justificativaDevolucao.value,
    });

    devolverModalVisible.value = false;
    devolverStatus.value = 'success';
  } catch (error: any) {
    devolverStatus.value = 'error';
    notification.error({
      message:
        error.response?.data?.errorMessage ||
        'Tivemos um problema ao tentar devolver o compromisso',
      duration: 5,
    });
  }
}

// Cleanup polling ao desmontar
onUnmounted(() => {
  stopPolling();
});
</script>

<template>
  <a-form
    v-if="showForm"
    ref="formRef"
    :model="model"
    autocomplete="off"
    layout="vertical"
  >
    <a-alert
      v-if="isLote"
      type="warning"
      message="Você irá realizar uma alteração em lote"
      show-icon
      style="margin-bottom: 24px"
    />

    <a-form-item
      label="Observação"
      name="observacao"
      :rules="[
        {
          message: 'Observação é obrigatória',
          required: true,
        },
      ]"
      style="padding-bottom: 0"
    >
      <a-textarea v-model:value="model.observacao" :rows="4" />
    </a-form-item>

    <Uploader
      ref="uploaderRef"
      v-model:file-list="model.arquivos"
      style="margin-top: 24px; margin-bottom: 24px"
    >
      <template #description>
        <p class="ant-upload-drag-icon">
          <CloudUploadOutlined />
        </p>
        <p class="ant-upload-text">Clique aqui ou arraste os arquivos</p>
        <p class="ant-upload-hint">Envie os arquivos necessários</p>
      </template>
    </Uploader>

    <a-form-item>
      <a-space>
        <a-button
          type="primary"
          :loading="isLoading"
          size="large"
          :disabled="buttonsDisabled"
          @click.prevent="handleSubmit"
        >
          <template #icon>
            <SaveOutlined />
          </template>
          {{ isLoading ? 'Processando...' : 'Salvar' }}
        </a-button>

        <a-button
          v-if="!isLote"
          danger
          size="large"
          :disabled="buttonsDisabled"
          @click="openDevolverModal"
        >
          <template #icon>
            <RollbackOutlined />
          </template>
          Devolver
        </a-button>
      </a-space>
    </a-form-item>
  </a-form>

  <!-- Modal de devolver -->
  <a-modal
    v-model:open="devolverModalVisible"
    title="Devolver Compromisso"
    :confirm-loading="devolverStatus === 'loading'"
    @ok="handleDevolver"
  >
    <a-form layout="vertical">
      <a-form-item label="Justifique a devolução" required>
        <a-textarea
          v-model:value="justificativaDevolucao"
          :rows="4"
          placeholder="Informe o motivo da devolução"
        />
      </a-form-item>
    </a-form>
  </a-modal>

  <!-- Resultado de sucesso devolver -->
  <a-result
    v-if="isDevolverSuccess"
    status="success"
    title="Compromisso devolvido com sucesso"
    sub-title="Agora você pode fechar essa janela"
  >
    <template #extra>
      <a-button @click="() => emit('close')">Fechar</a-button>
    </template>
  </a-result>

  <!-- Resultado de sucesso (simples) -->
  <a-result
    v-if="isSuccess && !isLote"
    status="success"
    title="Fluxo concluído com sucesso"
    sub-title="Agora você pode fechar essa janela"
  >
    <template #extra>
      <a-button @click="() => emit('close')">Fechar</a-button>
    </template>
  </a-result>

  <!-- Resultado de sucesso (lote) -->
  <a-result
    v-if="isSuccess && isLote"
    status="success"
    title="Fluxos concluídos com sucesso"
    :sub-title="`${result?.totalSucesso || 0} fluxo(s) processado(s) com sucesso`"
  >
    <template #extra>
      <a-list
        size="small"
        :data-source="result?.resultados || []"
        bordered
        style="margin-bottom: 16px; max-height: 300px; overflow-y: auto"
      >
        <template #renderItem="{ item }">
          <a-list-item
            style="
              display: flex;
              justify-content: space-between;
              align-items: center;
            "
          >
            <div style="text-align: left">
              <a :href="`/pasta/${item.codigo}`" target="_blank">
                {{ item.nome || item.codigo }}
              </a>
            </div>
            <CheckCircleOutlined style="color: #52c41a; margin-left: 8px" />
          </a-list-item>
        </template>
      </a-list>
      <a-button @click="() => emit('close')">Fechar</a-button>
    </template>
  </a-result>

  <!-- Resultado parcial (lote com falhas) -->
  <a-result
    v-if="isPartial"
    status="warning"
    title="Processamento parcial"
    :sub-title="`${result?.totalSucesso || 0} sucesso(s), ${result?.totalFalha || 0} falha(s)`"
  >
    <template #extra>
      <a-list
        size="small"
        :data-source="result?.resultados || []"
        bordered
        style="margin-bottom: 16px; max-height: 450px; overflow-y: auto"
      >
        <template #renderItem="{ item }">
          <a-list-item
            style="
              display: flex;
              justify-content: space-between;
              align-items: center;
            "
          >
            <div style="text-align: left; flex: 1">
              <a :href="`/pasta/${item.codigo}`" target="_blank">
                {{ item.nome || item.codigo }}
              </a>
              <span
                v-if="!item.sucesso"
                style="color: #ff4d4f; margin-left: 8px; font-size: 12px"
              >
                - {{ item.erro }}
              </span>
            </div>
            <CheckCircleOutlined
              v-if="item.sucesso"
              style="color: #52c41a; margin-left: 8px"
            />
            <ExclamationCircleOutlined
              v-else
              style="color: #ff4d4f; margin-left: 8px"
            />
          </a-list-item>
        </template>
      </a-list>
      <a-button @click="() => emit('close')">Fechar</a-button>
    </template>
  </a-result>
</template>
